// Module 1 — Submissions.
// Route: /reinsurance/submissions
//
// Intake and market preparation. The list is deals that have entered intake;
// Placement and Closings are the same records further along. See
// backend/controllers/reinsuranceDealStageController.js for why the three
// screens read one collection rather than three.

import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModulePage from './ModulePage';
import SubmissionDrawer from './SubmissionDrawer';
import {
  C, Icons, Kpi, Pill, ProgressCell,
  PrimaryButton, GhostButton, TwoLine, date, humanise,
} from './kit';

// Treaty or facultative. A quiet outline chip rather than a status pill:
// this is what the risk IS, not where it has got to, and giving it the same
// weight as the status column makes the row harder to scan, not easier.
function TypeChip({ value }) {
  if (!value) return <span style={{ color: C.faint }}>—</span>;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: 6,
      border: `1px solid ${C.border}`, background: C.panel,
      fontSize: 11.5, fontWeight: 600, color: C.body, whiteSpace: 'nowrap',
    }}>
      {humanise(value)}
    </span>
  );
}

const submissionForm = (t) => ({
  title: t('screens.reSubmissions.newTitle', 'New submission'),
  subtitle: t('screens.reSubmissions.newSub', 'Take a risk in by hand'),
  submitLabel: t('screens.reSubmissions.createBtn', 'Create submission'),
  endpoint: '/reinsurance/submissions',
  fields: [
    { key: 'cedantName', label: t('screens.reSubmissions.fCedantIn', 'Cedant'), required: true,
      placeholder: 'Gulf Petrochemicals W.L.L.' },
    { key: 'insuredName', label: t('screens.reSubmissions.fInsured', 'Original insured'),
      placeholder: 'Warehouse complex, Industrial Area' },
    { key: 'dealType', label: t('screens.reSubmissions.fTypeIn', 'Type'), type: 'select', required: true,
      defaultValue: 'facultative',
      options: [
        { value: 'treaty', label: 'Treaty' },
        { value: 'facultative', label: 'Facultative' },
      ] },
    { key: 'lineOfBusiness', label: t('screens.reSubmissions.fClassIn', 'Class of business'), required: true,
      placeholder: 'Property' },
    { key: 'geography', label: t('screens.reSubmissions.fGeoIn', 'Geography'), placeholder: 'Bahrain' },
    { key: 'layerLabel', label: t('screens.reSubmissions.fCoverIn', 'Requested cover'),
      placeholder: 'Per Risk, USD 10M xs USD 5M' },
    { key: 'sumInsured', label: t('screens.reSubmissions.fTivIn', 'Sum insured'), type: 'number',
      placeholder: '25000000' },
    { key: 'currency', label: t('screens.reSubmissions.fCurrencyIn', 'Currency'), defaultValue: 'USD' },
    { key: 'periodStart', label: t('screens.reSubmissions.fStartIn', 'Inception'), type: 'date' },
    { key: 'leadBrokerName', label: t('screens.reSubmissions.fBrokerIn', 'Lead broker / handler'),
      placeholder: 'Al Rashid Insurance Services W.L.L.' },
    { key: 'submissionStatus', label: t('screens.reSubmissions.fStatusIn', 'Status'), type: 'select',
      defaultValue: 'reviewing',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'reviewing', label: 'Reviewing' },
        { value: 'awaiting_info', label: 'Awaiting Info' },
        { value: 'urgent', label: 'Urgent' },
      ] },
  ],
  toPayload: (v) => ({
    cedantName: v.cedantName.trim(),
    ...(v.insuredName ? { insuredName: v.insuredName.trim() } : {}),
    dealType: v.dealType,
    lineOfBusiness: v.lineOfBusiness.trim(),
    ...(v.geography ? { geography: v.geography.trim(), territory: v.geography.trim() } : {}),
    ...(v.layerLabel ? { layerLabel: v.layerLabel.trim() } : {}),
    ...(v.sumInsured !== '' ? { sumInsured: Number(v.sumInsured) } : {}),
    currency: (v.currency || 'USD').trim().toUpperCase(),
    ...(v.periodStart ? { periodStart: v.periodStart } : {}),
    ...(v.leadBrokerName ? { leadBrokerName: v.leadBrokerName.trim(), handlerName: v.leadBrokerName.trim() } : {}),
    submissionStatus: v.submissionStatus || 'reviewing',
  }),
});

export default function SubmissionsList() {
  const { t } = useTranslation();
  const history = useHistory();
  // Two ways in, and they genuinely differ: "Import slip" hands the document
  // to the AI intake, "New submission" opens the form for a risk you already
  // have in front of you. Same record at the end of either.
  const openIntake = () => history.push('/reinsurance/submissions/intake');

  const columns = [
    { key: 'reference', label: t('screens.reSubmissions.colRef', 'Submission ref'), width: 150,
      render: (r) => <span style={{ fontWeight: 600, color: C.ink }}>{r.reference}</span> },
    { key: 'cedantName', label: t('screens.reSubmissions.colCedant', 'Cedant / client'),
      render: (r) => <TwoLine primary={r.cedantName} secondary={r.insuredName} /> },
    { key: 'dealType', label: t('screens.reSubmissions.colType', 'Type'), width: 104,
      render: (r) => <TypeChip value={r.dealType} /> },
    { key: 'lineOfBusiness', label: t('screens.reSubmissions.colClass', 'Class of business'),
      render: (r) => r.lineOfBusiness || '—' },
    { key: 'geography', label: t('screens.reSubmissions.colGeography', 'Geography'), width: 96,
      render: (r) => r.geography || r.territory || '—' },
    { key: 'periodStart', label: t('screens.reSubmissions.colRenewal', 'Renewal / effective'), width: 116,
      render: (r) => date(r.periodStart) },
    { key: 'handlerName', label: t('screens.reSubmissions.colBroker', 'Lead broker / handler'),
      render: (r) => r.leadBrokerName || r.handlerName || <span style={{ color: C.faint }}>unassigned</span> },
    { key: 'targetMarkets', label: t('screens.reSubmissions.colMarkets', 'Target markets'),
      render: (r) => (r.targetMarkets || []).join(', ') || <span style={{ color: C.faint }}>none set</span> },
    { key: 'documentProgress', label: t('screens.reSubmissions.colDocs', 'Docs'), width: 90,
      render: (r) => <ProgressCell done={r.documentProgress?.done || 0} total={r.documentProgress?.total || 0} /> },
    { key: 'submissionStatus', label: t('screens.reSubmissions.colStatus', 'Status'), width: 130,
      render: (r) => <Pill status={r.submissionStatus} /> },
  ];

  return (
    <ModulePage
      title={t('screens.reSubmissions.title', 'Submissions')}
      subtitle={t('screens.reSubmissions.subtitle', 'Intake and market preparation for treaty and facultative reinsurance')}
      endpoint='/reinsurance/submissions'
      searchPlaceholder={t('screens.reSubmissions.search', 'Search submissions, cedants, or reference…')}
      createForm={submissionForm(t)}
      actions={({ openCreate }) => (
        <>
          <GhostButton icon={Icons.clip()} onClick={openIntake}>{t('screens.reSubmissions.import', 'Import slip')}</GhostButton>
          <PrimaryButton onClick={openCreate}>{t('screens.reSubmissions.new', 'New submission')}</PrimaryButton>
        </>
      )}
      renderKpis={(s) => (
        <>
          <Kpi icon={Icons.doc()} tone='blue'
            value={s?.openSubmissions?.value ?? '—'} trend={s?.openSubmissions?.trend}
            label={t('screens.reSubmissions.kpiOpen', 'Open submissions')} />
          <Kpi icon={Icons.send()} tone='green'
            value={s?.pendingMarketApproach?.value ?? '—'} trend={s?.pendingMarketApproach?.trend}
            label={t('screens.reSubmissions.kpiPending', 'Pending market approach')} />
          <Kpi icon={Icons.alert()} tone='red'
            value={s?.urgentRenewals?.value ?? '—'} trend={s?.urgentRenewals?.trend}
            label={t('screens.reSubmissions.kpiUrgent', 'Urgent renewals')} />
          <Kpi icon={Icons.clip()} tone='amber'
            value={s?.missingDocuments?.value ?? '—'} trend={s?.missingDocuments?.trend}
            comparison={s?.missingDocuments?.comparison}
            label={t('screens.reSubmissions.kpiDocs', 'Missing documents')} />
        </>
      )}
      filters={[
        // The placement pipeline, which is the axis the dashboard's
        // "Open Submissions" and "In Market" cards count. Without it a
        // click-through from either card could not reproduce its own number.
        { key: 'pipeline', label: 'Pipeline', options: [
          { value: 'all', label: 'Whole pipeline' },
          { value: 'open', label: 'Open (draft or submitted)' },
          { value: 'in_market', label: 'In market' },
          { value: 'bound', label: 'Bound' },
        ] },
        { key: 'status', label: 'Status', options: [
          { value: 'all', label: 'All statuses' },
          { value: 'draft', label: 'Draft' },
          { value: 'reviewing', label: 'Reviewing' },
          { value: 'awaiting_info', label: 'Awaiting Info' },
          { value: 'ready_for_placement', label: 'Ready for Placement' },
          { value: 'urgent', label: 'Urgent' },
          { value: 'withdrawn', label: 'Withdrawn' },
        ] },
        { key: 'type', label: 'Type', options: [
          { value: 'all', label: 'All types' },
          { value: 'treaty', label: 'Treaty' },
          { value: 'facultative', label: 'Facultative' },
        ] },
      ]}
      columns={columns}
      emptyMessage={t('screens.reSubmissions.empty', 'No submissions in intake. Import a slip to start one.')}
      drawerTabs={['Overview', 'Documents', 'Activity', 'Markets']}
      drawerHeader={(r) => ({
        title: r.reference,
        badge: <Pill status={r.submissionStatus} />,
        lines: [r.cedantName, [humanise(r.dealType), r.lineOfBusiness].filter(Boolean).join(' · ')].filter(Boolean),
      })}
      drawer={(r, { tab, reload }) => (
        <SubmissionDrawer row={r} tab={tab} reload={reload} />
      )}
    />
  );
}
